'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { RiEyeLine, RiEyeOffLine } from '@remixicon/react';
import { useRef, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { signInWithEmailAndPasswordAction } from '@/features/auth/presentation/actions/auth-actions';
import { Loading } from '@/shared/presentation/components/loading';
import { cn } from '@/shared/utils/utils';

const formSchema = z.object({
  email: z.string().email({ message: 'El correo es invalido' }).min(2).max(50),
  password: z.string().min(1, 'La contraseña es invalida').max(100),
});

export function LoginForm() {
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isPending, startTransition] = useTransition();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setError(null);

    startTransition(async () => {
      const callbackUrl = new URLSearchParams(window.location.search).get('callbackUrl') ?? undefined;
      const result = await signInWithEmailAndPasswordAction(values.email, values.password, callbackUrl);

      if (result?.error) {
        setError(result.error);
        timeoutRef.current = setTimeout(() => {
          setError(null);
        }, 5000);
      } else if (result?.url) {
        window.location.href = result.url;
      }
    });
  }

  return (
    <Form {...form}>
      <form className="p-6 md:p-8" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex h-full w-full flex-col items-center justify-center gap-6">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-2xl font-bold" data-testid="login-heading">
              Bienvenido de nuevo
            </h1>
            <p className="text-balance text-muted-foreground">Inicia sesión en tu cuenta de OrderFast</p>
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input autoComplete="email" placeholder="mail@mail.com" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Contraseña</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Button
                      className="absolute right-0 h-full w-8"
                      size="icon"
                      tabIndex={-1}
                      variant="ghost"
                      onClick={e => {
                        e.preventDefault();
                        setShowPassword(!showPassword);
                      }}
                    >
                      <RiEyeOffLine
                        className={cn('scale-100 rotate-0 transition-all', {
                          'scale-0 -rotate-90': showPassword,
                        })}
                      />
                      <RiEyeLine
                        className={cn('absolute scale-0 rotate-90 transition-all', {
                          'scale-100 rotate-0': showPassword,
                        })}
                      />
                      <span className="sr-only">Toggle password</span>
                    </Button>
                    <Input
                      autoComplete="current-password"
                      placeholder="********"
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {error && <FormMessage className="text-destructive">{error}</FormMessage>}
          <Button className="w-full" disabled={isPending} type="submit">
            {isPending ? <Loading /> : 'Iniciar sesión'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
